import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { UserCog, Save, Check } from "lucide-react";
import CountryCityPicker from "./CountryCityPicker";
import ThemedPhoneInput from "./ThemedPhoneInput";
import { Country } from "country-state-city";

/**
 * Edit Personal Info dialog for logged-in players.
 * Fields: Full Name, Phone, Country, City.
 * On save: PATCH /api/auth/me, then refresh() and toast success.
 */
export default function EditProfileDialog({ trigger, onSaved }) {
  const { user, refresh } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", country: null, city: "" });

  // Prefill when opened
  useEffect(() => {
    if (!open || !user) return;
    setSaved(false);
    let country = null;
    if (user.country) {
      const iso2 =
        user.country_code ||
        Country.getAllCountries().find((c) => c.name === user.country)?.isoCode ||
        "";
      country = iso2 ? { name: user.country, iso2 } : { name: user.country, iso2: "" };
    }
    setForm({
      name: user.name || "",
      phone: user.phone || "",
      country,
      city: user.city || "",
    });
  }, [open, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(t("register.fullName") + " ✱");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone || null,
        country: form.country?.name || null,
        country_code: form.country?.iso2 || null,
        city: form.city || null,
      };
      await api.patch("/auth/me", payload);
      await refresh();
      setSaved(true);
      toast.success(t("editProfile.savedToast"));
      onSaved?.();
      setTimeout(() => setOpen(false), 900);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const inputCls = "mt-2 bg-transparent border-white/20 rounded-sm h-11 focus:border-[#e65f2b]";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild data-testid="edit-profile-trigger-wrap">
        {trigger || (
          <Button
            data-testid="edit-profile-btn"
            variant="outline"
            className="border-white/25 text-white hover:bg-white/10 rounded-sm h-10 uppercase tracking-widest"
          >
            <UserCog size={14} className="mr-2" />{t("editProfile.button")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[#0f0f0f] border border-white/10 text-white max-w-lg" data-testid="edit-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">
            {t("editProfile.title")}
          </DialogTitle>
          <p className="text-xs text-white/60 mt-1">{t("editProfile.subtitle")}</p>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 mt-4">
          <div>
            <Label className="uppercase text-xs tracking-widest text-white/70">{t("register.fullName")}</Label>
            <Input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="edit-profile-name"
              className={inputCls}
            />
          </div>

          <div>
            <Label className="uppercase text-xs tracking-widest text-white/70">{t("register.phone")}</Label>
            <div className="mt-2">
              <ThemedPhoneInput
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                defaultCountry={form.country?.iso2 || "NG"}
                data-testid="edit-profile-phone"
              />
            </div>
          </div>

          <CountryCityPicker
            country={form.country}
            city={form.city}
            onChange={({ country, city }) => setForm({ ...form, country, city })}
          />

          <DialogFooter className="pt-2 flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              data-testid="edit-profile-cancel"
              className="border-white/20 hover:bg-white/10 rounded-sm h-10 uppercase tracking-widest"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="edit-profile-save"
              className="bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold uppercase tracking-widest rounded-sm h-10 flex-1"
            >
              {saved ? (
                <><Check size={14} className="mr-2" />{t("editProfile.saved")}</>
              ) : (
                <><Save size={14} className="mr-2" />{saving ? t("editProfile.saving") : t("common.save")}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
